import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { first, map } from 'rxjs/operators'

import { Membership } from '../models/membership'
import { MembershipDefinition } from '../models/membership-definition'
import { Persona } from '../models/persona'

import { ApiService } from './api.service'
import { Mapper } from './mapper.service'
import { Store } from './store'
import { HttpClient } from '@angular/common/http'
import { environment } from 'environments/environment'
import { ElectronicDocumentAcceptance } from '../models/electronic-document-acceptance'

const REQUEST_OPTIONS = {
  withCredentials: true,
  headers: {
    Accept: 'application/json'
  }
}

@Injectable({
  providedIn: 'root'
})
export class MembershipService extends Store<Membership> {

  constructor(
    private api: ApiService,
    private http: HttpClient
  ) {
    super(new Membership({ }))
  }

  public find(id: string): Observable<Membership> {
    return this.api.get(`memberships/${id}`)
      .pipe(first())
      .pipe(map(data => {
        const mem = Mapper.as<Membership>(data.result, Membership)
        mem.metadata = data.metadata
        return mem
      }))
  }

  public findAll(ids: string[]): Observable<Membership[]> {
    return this.api.get('memberships', { params: { 'id[]': ids} })
      .pipe(first())
      .pipe(map(data => {
        return Mapper.asArray<Membership>(data.result, Membership)
      }))
  }

  public setCurrentMembershipData(membershipData: any): void {
    const membership = new Membership(membershipData)
    this.subject.next(membership)
  }

  public setCurrentMembership(membership: Membership): void {
    this.subject.next(membership)
  }

  public setCurrentMembershipDefinition(memdef: MembershipDefinition): void {
    this.subject.value.membership_definition = memdef
    this.subject.next(this.subject.value)
  }

  public setCurrentPersona(persona: Persona): void {
    this.subject.value.persona = persona
    this.subject.next(this.subject.value)
  }

  public setElectronicDocumentAcceptances(acceptances: ElectronicDocumentAcceptance[]): void {
    this.subject.value.electronicDocumentAcceptances = acceptances
    this.subject.next(this.subject.value)
  }

  public verifyMembershipFromSaleNumber(saleNumber: string): Observable<any> {
    return this.api.get('memberships/verify/' + saleNumber)
  }

  public getMemberships(orgId: number, data?: any): Observable<any> {
    data = data || { }
    data.boss_organization_id = orgId
    data.include_waivers = true
    return this.api.get('memberships', { params: data })
  }

  public purchaseForMinor(
    email: string,
    memDefId: string,
    personaId: number,
    effectiveAt?: string,
    affiliationOrgId?: number
  ): Observable<any> {
    const data: any = {
      email_address: email,
      membership_definition_id: memDefId,
      persona_id: personaId,
    }
    if (effectiveAt) data.effective_at = effectiveAt
    if (affiliationOrgId) data.affiliation_organization_id = affiliationOrgId
    return this.http.post(environment.membershipServiceUrl + 'api/memberships/purchase_for_minor_email', data, REQUEST_OPTIONS)
  }

  public purchaseInviteForMinor(email: string, memInviteId: string, effective_at?: string, memDefId?: string): Observable<any> {
    const data: any = {
      email_address: email,
      membership_invite_id: memInviteId
    }
    if (effective_at) data.effective_at = effective_at
    if (memDefId) data.membership_definition_id = memDefId
    return this.http.post(environment.membershipServiceUrl + 'api/memberships/purchase_for_minor_email', data, REQUEST_OPTIONS)
  }

  public updateAffiliation(id: string, affiliationOrgId: number): Observable<any> {
    const data: any = { affiliation_organization_id: affiliationOrgId }
    return this.api.put(`memberships/${id}/mine`, data)
  }
}
